import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { cssSpacing } from '@spotify-internal/encore-web';
import { FilterItem } from '@helpers/filterItem';
import { FilterChipButton } from './FilterChipButton';
import { FilterFlyout } from './FilterFlyout';

const TriggerWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const FlyoutPosition = styled.div`
  position: absolute;
  top: calc(100% + ${cssSpacing('tighter')});
  left: 0;
  z-index: 1000;
`;

export interface FilterDropdownProps {
  label: string;
  items: FilterItem[];
  selectedValues: string[];
  onApply: (selectedValues: string[]) => void;
  helpText?: string;
  'aria-label'?: string;
  name: string;
}

export const FilterDropdown = ({
  label,
  items,
  selectedValues,
  onApply,
  helpText,
  'aria-label': ariaLabel,
  name,
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close on click-outside or Escape; restore focus to trigger on cleanup
  useEffect(() => {
    if (!isOpen) return undefined;

    const triggerButton = buttonRef.current;

    const handleMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      triggerButton?.focus();
    };
  }, [isOpen]);

  return (
    <TriggerWrapper ref={wrapperRef}>
      <FilterChipButton
        count={selectedValues.length}
        isOpen={isOpen}
        onClick={() => setIsOpen(prev => !prev)}
        ref={buttonRef}
      >
        {label}
      </FilterChipButton>
      {isOpen && (
        <FlyoutPosition>
          <FilterFlyout
            aria-label={ariaLabel}
            name={name}
            items={items}
            initialSelection={selectedValues}
            onApply={selected => {
              onApply(selected);
              setIsOpen(false);
            }}
            helpText={helpText}
          />
        </FlyoutPosition>
      )}
    </TriggerWrapper>
  );
};
